import React, { useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import cn from 'classnames';

import { useOnClickOutside } from '@hooks/utils/useOnClickOutSide';

import { IActionMenu } from '@interfaces/components/table';

import styles from './FolderItemActionsMenu.module.scss';

interface IFolderItemActionsMenuProps<T> {
	anchorRef: React.RefObject<HTMLElement>;
	actions: IActionMenu<T>[];
	isVisibleMenu: boolean;
	onClick: (actionId: T) => void;
	onClose: () => void;
}

const FolderItemActionsMenu = <T, >({
	anchorRef,
	actions,
	isVisibleMenu,
	onClick,
	onClose,
}: IFolderItemActionsMenuProps<T>) => {
	const menuRef = useRef<HTMLDivElement>(null);

	const [menuCoords, setMenuCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
	const [menuPositionAbove, setMenuPositionAbove] = useState(false);

	useLayoutEffect(() => {
		if (!(anchorRef.current && menuRef.current && isVisibleMenu)) {
			return;
		}

		const anchorRect = anchorRef.current.getBoundingClientRect();
		const menuHeight = menuRef.current.offsetHeight;
		const menuWidth = menuRef.current.offsetWidth;

		const spaceBelow = window.innerHeight - anchorRect.bottom;
		const spaceAbove = anchorRect.top;

		const shouldFlip = spaceBelow < menuHeight && spaceAbove > menuHeight;

		setMenuCoords({
			top: shouldFlip
				? anchorRect.top + window.scrollY - menuHeight
				: anchorRect.bottom + window.scrollY,
			left: anchorRect.right - menuWidth + window.screenX,
		});

		setMenuPositionAbove(shouldFlip);
	}, [isVisibleMenu, anchorRef]);

	useOnClickOutside({
		refs: [menuRef],
		handler: onClose,
		fireEvent: 'mousedown',
		disableClickOutside: !isVisibleMenu,
	});

	const handleActionClick = (action: IActionMenu<T>) => (e: React.MouseEvent) => {
		e.stopPropagation();

		if (!action.disabled) {
			onClick(action.id);
		}
	};

	if (!isVisibleMenu) return null;

	return ReactDOM.createPortal(
		<div
			className={cn(styles.menusContainer, {
				[styles.positionAbove]: menuPositionAbove,
			})}
			style={{
				top: menuCoords.top,
				left: menuCoords.left,
			}}
		>
			<div
				ref={menuRef}
				className={styles.menu}
			>
				{actions
					.filter((action) => !action.hidden)
					.map(action => {
						return (
							<div
								key={action.text}
								className={cn(styles.menuItem, {
									[styles.disabled]: action.disabled,
								})}
								onMouseDown={handleActionClick(action)}
							>
								{action.text}
							</div>
						);
					})
				}
			</div>
		</div>,
		document.body,
	);
};

export default FolderItemActionsMenu;
